import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Search, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "Qual é o horário e a morada da oficina?",
  "Que serviços oferecem?",
  "Como posso pedir um orçamento?",
  "Que marcas de pneus têm disponíveis?",
];

const LINK_RE = /\[([^\]]+)\]\((\/[a-z0-9\-/]*)\)|(?:\((?:ver (?:mais )?em|página|em)\s+)?(\/(?:sobre|faq|servicos|precario|pneus|simulador|guias(?:\/[a-z0-9-]+)?|orcamento|login|minha-conta|os-meus-orcamentos))\)?/g;

const renderWithLinks = (text, onNavigate) => {
  const parts = [];
  let last = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const url = m[2] || m[3];
    parts.push(
      <Link
        key={`${m.index}-${url}`}
        to={url}
        onClick={onNavigate}
        data-testid="chat-inline-link"
        className="inline-flex items-center gap-0.5 text-[#5FA8FF] underline underline-offset-2 decoration-[#5FA8FF]/50 hover:text-white hover:decoration-white font-semibold"
      >
        ver aqui
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Olá! Sou a assistente virtual da Global Pneus. Em que posso ajudar?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const newHistory = [...messages, { role: "user", text: msg }];
    setMessages(newHistory);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
        history: newHistory.slice(-6),
      }, { timeout: 20000 });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, sources: r.data.sources || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Desculpe, ocorreu um problema. Tente novamente ou ligue para +351 219 592 340." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fechar chat" : "Falar com a assistente"}
        className={`fixed right-6 bottom-28 md:bottom-32 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#0E2452] border-2 border-[#FF5A00] text-white hover:bg-[#FF5A00] transition-all active:scale-95 flex items-center justify-center ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        style={{ boxShadow: "0 8px 24px rgba(255, 90, 0, 0.4), 0 0 0 3px rgba(255,255,255,0.08)" }}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.2} />}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed right-4 md:right-6 bottom-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[min(75vh,580px)] flex flex-col bg-[#0B1D44] border border-[#2F568F]/70 rounded-2xl overflow-hidden"
          style={{ animation: "chatSlideIn 250ms ease-out", boxShadow: "0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,90,0,0.08)" }}
        >
          <div className="relative flex items-center justify-between gap-2 px-4 py-3 border-b border-[#2F568F]/60 bg-gradient-to-r from-[#0E2452] via-[#123063] to-[#0E2452]">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-full bg-[#FF5A00]/15 border border-[#FF5A00] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#FF5A00]" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0E2452]" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold uppercase text-white text-[13px] tracking-wider">Assistente Global Pneus</div>
                <div className="flex items-center gap-1.5 text-[10px] text-emerald-300/90 font-body">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online agora
                </div>
              </div>
            </div>
            <button type="button" data-testid="chat-widget-close" onClick={() => setOpen(false)} aria-label="Fechar chat" className="w-8 h-8 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#081736]">
            {messages.map((m, i) => (
              <div key={`${i}-${m.role}-${m.text.slice(0, 24)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm font-body leading-relaxed whitespace-pre-line shadow-sm ${
                    m.role === "user"
                      ? "bg-[#FF5A00] text-white rounded-2xl rounded-br-md"
                      : "bg-[#12305F] text-white/95 border border-[#2F568F]/60 rounded-2xl rounded-bl-md"
                  }`}
                >
                  {m.role === "assistant" ? renderWithLinks(m.text, () => setOpen(false)) : m.text}
                  {m.role === "assistant" && m.sources?.length > 0 && (
                    <div className="mt-2.5 pt-2 border-t border-white/10" data-testid={`chat-sources-${i}`}>
                      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40 font-display mb-1.5">
                        <BookOpen className="w-3 h-3" /> Fontes no site
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {m.sources.map((src) => (
                          <Link
                            key={src.url + src.title}
                            to={src.url}
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-[#5FA8FF]/40 bg-[#5FA8FF]/10 text-[#5FA8FF] hover:bg-[#5FA8FF] hover:text-[#0E2452] transition-colors"
                          >
                            {src.page}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#12305F] border border-[#2F568F]/60 rounded-2xl rounded-bl-md px-3.5 py-2.5 flex gap-1.5 items-center">
                  <Search className="w-3.5 h-3.5 text-[#5FA8FF] animate-pulse mr-1" />
                  <span className="text-[11px] text-white/60 font-body mr-1">A pesquisar no site</span>
                  <span className="w-1.5 h-1.5 bg-[#FF5A00] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-[#FF5A00] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-[#FF5A00] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="pt-2 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-display px-1">Sugestões rápidas</div>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-full border border-[#2F568F]/70 bg-[#0E2452]/60 text-white/85 hover:border-[#FF5A00] hover:bg-[#FF5A00]/10 hover:text-white transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3 border-t border-[#2F568F]/60 bg-[#0E2452]">
            <input
              data-testid="chat-widget-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escreva a sua pergunta..."
              maxLength={500}
              disabled={loading}
              className="flex-1 bg-[#0B1D44] border border-[#2F568F]/70 rounded-full text-white text-sm px-4 py-2.5 focus:outline-none focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/20 disabled:opacity-50 placeholder:text-white/40"
            />
            <button
              type="submit"
              data-testid="chat-widget-send"
              disabled={loading || !input.trim()}
              aria-label="Enviar mensagem"
              className="w-11 h-11 shrink-0 rounded-full bg-[#FF5A00] hover:bg-[#E65100] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center shadow-lg shadow-[#FF5A00]/30"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          <div className="text-[9px] uppercase tracking-widest text-white/30 text-center py-1.5 bg-[#0B1D44] font-display">
            Respostas de IA baseadas na pesquisa do conteúdo do site
          </div>

          <style>{`
            @keyframes chatSlideIn {
              from { opacity: 0; transform: translateY(12px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
